import React, { CSSProperties, useMemo } from 'react';
import { themeColours, themeType } from 'fym-shared/src/music24/releaseThemes';
import {
  artworkMotionBarGap,
  artworkMotionBarStyle,
  artworkMotionLayout,
  artworkMotionParticles,
  artworkMotionPlacement,
  shortVideoHaze,
  type ArtworkMotionShape,
} from 'fym-shared/src/music24/story/artworkMotionVideo';
import { storyFrameOf } from 'fym-shared/src/music24/story/storyFrames';
import {
  storyCoverFrame,
  storyDeco,
  storyGroundBackground,
  storyRecord,
  storyTitleLineStyle,
  type StoryColours,
} from 'fym-shared/src/music24/story/storyStyles';
import type { KitStory } from 'fym-shared/src/music24/story/storyTypes';
import { watermarkTone } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import { kitFontName, useKitWebFonts } from '../../themes/kitWebFonts';
import type { StoryPreviewContext } from '../details/imageTiles/storyPreviewContext';
import { StoryPreviewText, type StoryFontCss } from './StoryPreviewText';
import styles from './StoryPreview.module.scss';
import { WatermarkLockup } from './WatermarkLockup';

/** A still level of each of the 16 bars: the shape of a song spectrum (the preview has no song). */
const SAMPLE_LEVELS = [0.2, 0.4, 0.6, 0.5, 0.3, 0.6, 0.7, 0.4, 0.3, 0.5, 0.6, 0.4, 0.3, 0.2, 0.3, 0.2];
const FULL: CSSProperties = { position: 'absolute', inset: 0 };

function fontCss(fontId: Parameters<typeof kitFontName>[0], style: ReturnType<typeof themeType>['text']): StoryFontCss {
  return {
    fontFamily: kitFontName(fontId),
    fontWeight: style.weight,
    fontStyle: style.italic ? 'italic' : 'normal',
    textTransform: style.case === 'none' ? 'none' : style.case,
    letterSpacing: `${style.tracking}em`,
    tracking: style.tracking,
  };
}

interface MotionVideoPreviewProps {
  context: StoryPreviewContext;
  shape: ArtworkMotionShape;
  /** The Particles style draws its own layer in place of the bars (design q). */
  particlesStyle?: boolean;
  /** The pill of the file: it follows the date and the pre-save page. */
  cta: string;
  width: number;
}

/**
 * The step-3 preview of the artwork in motion (design `asset-redesign/q`): one frame with the CTA
 * in, drawn with the layout of the render (fym-shared `artworkMotionLayout`): the framed cover, the
 * 16 bars in the theme shape at a still level, the id line, the pill and the SmartLink line.
 */
const MotionVideoPreviewComponent: React.FC<MotionVideoPreviewProps> = ({
  context,
  shape,
  cta,
  width,
  particlesStyle = false,
}) => {
  useKitWebFonts();
  const { look, palette } = context;
  const { themeId } = look;
  const type = useMemo(() => themeType(look), [look]);
  const colors = useMemo<StoryColours>(() => {
    const colours = themeColours(themeId, palette);
    return {
      ink: colours.background,
      surface: colours.surface,
      text: colours.text,
      accent: colours.accent,
      onAccent: colours.onAccent,
    };
  }, [palette, themeId]);
  const video = useMemo(
    () => ({ shape, title: context.title, artist: context.artist, cta, link: context.link }),
    [context.artist, context.link, context.title, cta, shape]
  );
  const layout = useMemo(
    () =>
      artworkMotionLayout({
        video,
        themeId,
        fonts: type.fonts,
        typeStyle: { display: type.display, text: type.text },
        watermark: context.watermark,
      }),
    [context.watermark, themeId, type, video]
  );
  const story: KitStory = useMemo(
    () => ({
      moment: 'outNow',
      title: video.title,
      artist: video.artist,
      kicker: null,
      hero: null,
      nowWord: null,
      dayLabel: null,
      when: null,
      cta,
      link: video.link,
      linkNote: null,
      withLogo: false,
    }),
    [cta, video]
  );
  const display = useMemo(() => fontCss(type.fonts.display, type.display), [type]);
  const text = useMemo(() => fontCss(type.fonts.text, type.text), [type]);
  const { format, cover, bars, id } = layout;
  const { unit } = format;
  // The 9:16 stack of design q; a 4:5 or square file shows it scaled into its safe column.
  const placement = artworkMotionPlacement(shape);
  const file = placement.format;
  const scale = width / file.size.width;
  const frame = storyFrameOf(themeId);
  const ground = storyGroundBackground(themeId, colors);
  const particles = particlesStyle ? artworkMotionParticles(4, 15, bars.box, SAMPLE_LEVELS) : null;
  const coverFrame = storyCoverFrame(themeId, false, colors, format.id === 'video916' ? 1 : unit);
  const { tracking: _display, ...displayCss } = display;
  const { tracking: _text, ...textCss } = text;

  return (
    <div className={styles.frame} style={{ width, height: file.size.height * scale }} data-testid="motion-preview">
      <div
        className={styles.canvas}
        style={{
          width: file.size.width,
          height: file.size.height,
          transform: `scale(${scale})`,
          background: colors.ink,
          color: colors.text,
        }}
      >
        {ground !== null && <div style={{ ...FULL, background: ground }} />}
        <div style={{ ...FULL, opacity: 0.6, background: shortVideoHaze(colors) }} />
        {storyDeco(themeId, colors, file.id).map((deco) => (
          <div key={JSON.stringify(deco)} style={deco} />
        ))}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: format.size.width,
            height: format.size.height,
            transform: `translate(${placement.x}px, ${placement.y}px) scale(${placement.scale})`,
            transformOrigin: '0 0',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: cover.box.x,
              top: cover.box.y,
              width: cover.box.w,
              boxSizing: 'border-box',
              ...coverFrame.outer,
            }}
          >
            {cover.withRecord && <div style={storyRecord(colors, unit)} />}
            {context.artworkUrl === undefined ? (
              <div className={styles.picture} style={{ background: colors.accent, ...coverFrame.picture }} />
            ) : (
              <img className={styles.picture} src={context.artworkUrl} alt="" style={coverFrame.picture} />
            )}
            {coverFrame.tape !== null && <div style={coverFrame.tape} />}
          </div>
          <div
            style={{
              position: 'absolute',
              left: bars.box.x,
              top: bars.box.y,
              width: bars.box.w,
              height: bars.box.h,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: artworkMotionBarGap(themeId) * (bars.box.h / 110),
            }}
          >
            {particles === null
              ? SAMPLE_LEVELS.map((level, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- the bars are a fixed row
                  <i key={index} style={artworkMotionBarStyle(themeId, colors, index, level, bars.box.h)} />
                ))
              : particles.map((particle, index) => (
                  <i
                    // eslint-disable-next-line react/no-array-index-key -- the particles are a fixed set
                    key={index}
                    style={{
                      position: 'absolute',
                      left: particle.x,
                      top: particle.y,
                      width: particle.size,
                      height: particle.size,
                      borderRadius: '50%',
                      opacity: particle.opacity,
                      background: particle.colour === 'text' ? colors.text : colors.accent,
                    }}
                  />
                ))}
          </div>
          <div
            style={{
              position: 'absolute',
              left: id.line.x,
              top: id.line.y,
              width: id.line.w,
              height: id.line.h,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: id.gap,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ ...displayCss, fontSize: id.title.sizePx, lineHeight: 1 }}>
              <span style={storyTitleLineStyle(themeId, colors)}>{id.title.text}</span>
            </span>
            <span
              style={{
                flex: 'none',
                width: id.dot.w,
                height: id.dot.h,
                borderRadius: '50%',
                background: colors.accent,
              }}
            />
            <span style={{ ...textCss, fontSize: id.artist.sizePx, lineHeight: 1 }}>{id.artist.text}</span>
          </div>
          {layout.cta.map((item) =>
            item.kind === 'pill' || item.kind === 'link' ? (
              <StoryPreviewText
                key={`${item.kind}-${item.box.y}`}
                item={item}
                story={story}
                frame={frame}
                themeId={themeId}
                display={display}
                text={text}
                colors={colors}
                unit={unit}
              />
            ) : null
          )}
          {layout.watermark?.kind === 'watermark' && (
            <WatermarkLockup
              testId="motion-preview-watermark"
              lockup={layout.watermark.lockup}
              tone={watermarkTone(colors.ink, false)}
              x={layout.watermark.box.x}
              y={layout.watermark.box.y}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const MotionVideoPreview = TypedMemo(MotionVideoPreviewComponent);
